import { useState } from 'react'
import { DiffEditor } from '@monaco-editor/react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useCurrentUser, useSkillVersions } from '@/hooks'
import { formatDateShort } from '@/lib/timeFormatters'
import { cn } from '@/lib/utils'
import type { SkillDetail, SkillVersion } from '@/types'
import { SkillSectionActions } from './SkillSectionActions'

const INITIAL_VERSION = '1.0.0'

interface SkillVersionsPanelProps {
  projectId: string
  skill: SkillDetail | null
  internalName: string
  currentEditorContent: string
}

export function SkillVersionsPanel({ projectId, skill, internalName, currentEditorContent }: SkillVersionsPanelProps) {
  const { data: versions, isLoading, isError, refetch } = useSkillVersions(projectId, skill?.id)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [isComparing, setIsComparing] = useState(false)
  const { data: currentUser } = useCurrentUser()
  const currentUserName = currentUser && (`${currentUser.firstName ?? ''} ${currentUser.lastName ?? ''}`.trim() || currentUser.displayName)

  const versionsDesc = versions ?? []
  const latestVersionId = skill?.latestVersion?.id ?? versionsDesc[0]?.id
  const isDraft = skill !== null && currentEditorContent.trim() !== skill.content.trim()
  const selected = versionsDesc.find(version => version.id === selectedId) ?? versionsDesc[0] ?? null
  const canCompare = selected !== null && selected.id !== latestVersionId
  const showDiff = isComparing && canCompare

  return (
    <div className="flex h-full flex-col gap-4 rounded-2xl border border-border bg-card p-5" data-testid="skill-version-history">
      <div className="flex min-h-10 flex-wrap items-center justify-between gap-4">
        <h2 className="text-lg font-bold leading-[1.3] text-foreground">Version history</h2>
        {skill ? (
          canCompare && (
            <Button onClick={() => setIsComparing(value => !value)}>
              {isComparing ? 'Back to version' : 'Compare with current'}
            </Button>
          )
        ) : (
          <SkillSectionActions />
        )}
      </div>

      <div className="flex min-h-0 flex-1 gap-4">
        <ul className="flex w-[330px] shrink-0 flex-col gap-2 overflow-y-auto" aria-label="Skill versions">
          {skill === null && (
            <VersionCard version={INITIAL_VERSION} badge="Not saved yet" summary="Initial version" meta={currentUserName ? `${currentUserName} · Created when you save the skill` : 'Created when you save the skill'} isSelected />
          )}
          {skill !== null && isLoading && (
            <li className="flex flex-1 items-center justify-center text-sm text-muted-foreground">Loading...</li>
          )}
          {skill !== null && isError && (
            <li
              role="alert"
              data-testid="skill-version-history-error"
              className="flex flex-1 flex-col items-center justify-center gap-2 px-4 text-center text-sm text-destructive"
            >
              <span>Failed to load version history.</span>
              <button className="underline" onClick={() => refetch()}>Retry</button>
            </li>
          )}
          {skill !== null && !isLoading && !isError && versionsDesc.length === 0 && (
            <li className="flex flex-1 items-center justify-center text-sm text-muted-foreground">No versions yet</li>
          )}
          {versionsDesc.map((version, index) => (
            <VersionCard
              key={version.id}
              version={version.version}
              badge={version.id === latestVersionId ? 'Current' : 'Retained for replay'}
              summary={index === versionsDesc.length - 1 ? 'Initial version' : `Version ${version.version}`}
              meta={version.createdByName ? `${version.createdByName} · ${formatDateShort(version.createdAt)}` : formatDateShort(version.createdAt)}
              isSelected={version.id === selected?.id}
              onSelect={() => setSelectedId(version.id)}
              testId={`skill-version-${version.version}`}
            />
          ))}
        </ul>

        <div className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-2xl bg-brand-panel text-white" data-testid="skill-version-content">
          <p className="shrink-0 border-b border-white/10 px-5 py-4 font-mono text-sm">{internalName || 'skill'}/SKILL.md</p>
          {showDiff ? (
            <div className="flex min-h-0 flex-1 flex-col" data-testid="skill-version-diff">
              <div className="flex shrink-0 border-b border-white/10 text-xs font-semibold text-white/70">
                <span className="flex-1 px-5 py-1.5">Version {selected.version}</span>
                <span className="flex-1 border-l border-white/10 px-5 py-1.5" data-testid="skill-version-diff-modified-heading">
                  {isDraft ? 'Current draft (unsaved)' : 'Current'}
                </span>
              </div>
              <DiffEditor
                height="100%"
                language="markdown"
                theme="vs-dark"
                original={selected.contentSnapshot}
                modified={currentEditorContent}
                options={{
                  originalAriaLabel: `Version ${selected.version}`,
                  modifiedAriaLabel: isDraft ? 'Current draft, unsaved' : 'Current version',
                  readOnly: true,
                  renderSideBySide: true,
                  minimap: { enabled: false },
                  fontSize: 14,
                  wordWrap: 'on',
                  scrollBeyondLastLine: false,
                  automaticLayout: true,
                }}
              />
            </div>
          ) : (
            <pre
              className="min-h-0 flex-1 overflow-auto whitespace-pre-wrap px-5 py-6 font-mono text-sm leading-relaxed"
              data-testid="skill-version-view"
              aria-label={selected ? `Skill content, version ${selected.version}` : 'Skill content, not saved yet'}
            >
              {(selected ? selected.contentSnapshot : currentEditorContent) || (skill === null ? 'Your SKILL.md content appears here.' : '')}
            </pre>
          )}
        </div>
      </div>
    </div>
  )
}

interface VersionCardProps {
  version: SkillVersion['version']
  badge: 'Current' | 'Retained for replay' | 'Not saved yet'
  summary: string
  meta: string
  isSelected: boolean
  onSelect?: () => void
  testId?: string
}

function VersionCard({ version, badge, summary, meta, isSelected, onSelect, testId }: VersionCardProps) {
  const body = (
    <>
      <div className="mb-1.5 flex items-center gap-2">
        <span className="truncate text-sm font-bold text-foreground">@{version}</span>
        <Badge
          variant="outline"
          className={cn(
            'shrink-0 px-2 py-0 text-[10px] font-medium',
            badge === 'Current' ? 'border-success-foreground/40 bg-success text-success-foreground' : 'text-muted-foreground',
          )}
        >
          {badge}
        </Badge>
      </div>
      <p className="text-sm text-muted-foreground">{summary}</p>
      <p className="mt-1 text-xs italic text-muted-foreground">{meta}</p>
    </>
  )
  const className = cn(
    'block w-full rounded-xl border px-4 py-3 text-left transition-colors',
    isSelected ? 'border-foreground bg-muted/40' : 'border-border hover:bg-muted/40',
  )

  return (
    <li>
      {onSelect ? (
        <button type="button" onClick={onSelect} aria-current={isSelected} className={className} data-testid={testId}>
          {body}
        </button>
      ) : (
        <div className={className}>{body}</div>
      )}
    </li>
  )
}
